<template>
  <v-container fluid class="pa-0 settings-container">
    <v-row no-gutters class="fill-height">
      <!-- Settings Sidebar -->
      <v-col
        cols="12"
        md="3"
        lg="2"
        class="settings-sidebar"
        :class="{
          'sidebar-mobile-open': mobileNavOpen,
          'grey lighten-5': !$vuetify.theme.dark,
          'grey darken-4': $vuetify.theme.dark
        }"
      >
        <!-- Mobile Header -->
        <div class="d-md-none pa-4 d-flex align-center justify-space-between">
          <h2 class="text-h6 font-weight-medium">{{ $t('setting.name') }}</h2>
          <v-btn icon @click="mobileNavOpen = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Settings Navigation -->
        <v-list
          shaped
          class="settings-nav pa-2"
        >
          <v-list-item-group
            v-model="selectedSetting"
            color="primary"
          >
            <!-- Company Settings -->
            <v-subheader class="text-uppercase font-weight-bold mt-2">
              {{ $t('setting.groups.company') || 'Company' }}
            </v-subheader>

            <v-list-item
              v-can="'setting.organisation'"
              :to="{ name: 'config_org' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-office-building</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.organisation') || 'Organisation' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.organisation') || 'Company details and information' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              v-can="'setting.read'"
              :to="{ name: 'config_reg' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-certificate</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.registration') || 'Registration' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.registration') || 'Business registration details' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              :to="{ name: 'config_logo' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-image</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.logo') || 'Logo' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.logo') || 'Company logo management' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <!-- Sales & Commerce -->
            <v-subheader class="text-uppercase font-weight-bold mt-4">
              {{ $t('setting.groups.sales') || 'Sales & Commerce' }}
            </v-subheader>

            <v-list-item
              v-can="'setting.receipt'"
              :to="{ name: 'receipts' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-receipt</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.receipts') || 'Receipts' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.receipts') || 'Receipt templates and configuration' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              :to="{ name: 'print_terminals' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-printer</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.sales_print_terminals') || 'Print Terminals' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.print_terminals') || 'Configure printing destinations' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              v-can="'setting.pos'"
              :to="{ name: 'pos_settings' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-point-of-sale</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.pos') || 'POS Settings' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.pos') || 'Point of sale configuration' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <!-- Products & Inventory -->
            <v-subheader class="text-uppercase font-weight-bold mt-4">
              {{ $t('setting.groups.products') || 'Products & Inventory' }}
            </v-subheader>

            <v-list-item
              v-can="'setting.price-list'"
              :to="{ name: 'price_lists' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-currency-usd</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.price_lists') || 'Price Lists' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.price_lists') || 'Manage pricing structures' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              v-can="'setting.default-message'"
              :to="{ name: 'default_messages' }"
              class="mb-1 rounded"
            >
              <v-list-item-icon>
                <v-icon>mdi-message-text</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('setting.tabs.default_messages') || 'Default Messages' }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ $t('setting.desc.default_messages') || 'System message templates' }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-col>

      <!-- Settings Content Area -->
      <v-col
        cols="12"
        md="9"
        lg="10"
        class="settings-content"
      >
        <!-- Mobile Toggle Button -->
        <v-btn
          icon
          class="d-md-none ma-2"
          @click="mobileNavOpen = true"
          v-if="!mobileNavOpen"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <!-- Page Header -->
        <div class="settings-header pa-6 pb-4">
          <h1 class="text-h4 font-weight-medium mb-2">{{ pageTitle }}</h1>
          <p class="text-subtitle-1 mb-0" v-if="pageDescription">
            {{ pageDescription }}
          </p>
        </div>

        <!-- Content Area -->
        <div class="pa-6 pt-2">
          <transition name="fade-transform" mode="out-in">
            <router-view
              @parent-title="updateTitle"
              @parent-description="updateDescription"
            />
          </transition>
        </div>
      </v-col>
    </v-row>

    <!-- Mobile Backdrop -->
    <v-overlay
      v-model="mobileNavOpen"
      class="d-md-none"
      opacity="0.46"
      @click="mobileNavOpen = false"
    />
  </v-container>
</template>

<script>
export default {
  name: 'Settings',
  data () {
    return {
      pageTitle: this.$t('setting.name') || 'Settings',
      pageDescription: '',
      selectedSetting: null,
      mobileNavOpen: false
    }
  },
  methods: {
    updateTitle (title) {
      this.pageTitle = title
    },
    updateDescription (description) {
      this.pageDescription = description
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler () {
        // Close mobile nav when route changes
        this.mobileNavOpen = false

        // Update selected item based on route
        const routeName = this.$route.name
        const settingRoutes = [
          'config_org',
          'config_reg',
          'config_logo',
          'receipts',
          'print_terminals',
          'pos_settings',
          'price_lists',
          'default_messages'
        ]
        this.selectedSetting = settingRoutes.indexOf(routeName)
      }
    }
  },
  mounted () {
    // Set initial title if provided by route meta
    if (this.$route.meta && this.$route.meta.title) {
      this.pageTitle = this.$route.meta.title
    }
  }
}
</script>

<style scoped>
.settings-container {
  height: calc(100vh - 64px);
  overflow: hidden;
}

.settings-sidebar {
  height: 100%;
  overflow-y: auto;
  border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .settings-sidebar {
  border-right-color: rgba(255, 255, 255, 0.12);
}

.settings-content {
  height: 100%;
  overflow-y: auto;
  background-color: #fafafa;
}

.theme--dark .settings-content {
  background-color: #121212;
}

.settings-header {
  background-color: white;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.theme--dark .settings-header {
  background-color: #1e1e1e;
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.settings-nav .v-list-item {
  min-height: 56px;
}

.settings-nav .v-list-item--active {
  background-color: rgba(25, 118, 210, 0.08);
}

.theme--dark .settings-nav .v-list-item--active {
  background-color: rgba(25, 118, 210, 0.24);
}

.settings-nav .v-list-item__subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
  margin-top: 2px;
}

/* Mobile Sidebar */
@media (max-width: 959px) {
  .settings-sidebar {
    position: fixed;
    top: 0;
    left: -100%;
    width: 280px;
    height: 100vh;
    z-index: 6;
    transition: left 0.3s ease;
  }

  .settings-sidebar.sidebar-mobile-open {
    left: 0;
  }
}

/* Transitions */
.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all 0.3s ease;
}

.fade-transform-enter {
  opacity: 0;
  transform: translateX(-30px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
